import { useEffect, useState } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import {
  Plus, Search, Loader2, Pencil, Trash2, MoreHorizontal, FileText, Briefcase,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtCurrency = (v) =>
  v == null
    ? '-'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v);

const fmtPercent = (v) => (v == null ? '-' : `${Number(v).toFixed(2)}%`);

const emptyForm = {
  client_name: '',
  cabex_id: '',
  contract_date: '',
  payment_day: '',
  contract_amount: '',
  contract_fee: '',
  client_percentage: '',
  notes: '',
};

export default function CabexClientsPage() {
  const { currentCompany } = useCompany();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchClients = async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const params = { company_id: currentCompany.id };
      if (search) params.search = search;
      const res = await axios.get(`${API_URL}/api/cabex/clients`, { params, withCredentials: true });
      setClients(res.data);
    } catch {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id, search]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (client) => {
    setEditing(client);
    setForm({
      client_name: client.client_name || '',
      cabex_id: client.cabex_id || '',
      contract_date: client.contract_date || '',
      payment_day: client.payment_day != null ? String(client.payment_day) : '',
      contract_amount: client.contract_amount != null ? String(client.contract_amount) : '',
      contract_fee: client.contract_fee != null ? String(client.contract_fee) : '',
      client_percentage: client.client_percentage != null ? String(client.client_percentage) : '',
      notes: client.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.client_name.trim()) {
      toast.error('Client name is required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        company_id: currentCompany.id,
        client_name: form.client_name,
        cabex_id: form.cabex_id || null,
        contract_date: form.contract_date || null,
        payment_day: form.payment_day || null,
        contract_amount: form.contract_amount || null,
        contract_fee: form.contract_fee || null,
        client_percentage: form.client_percentage || null,
        notes: form.notes || null,
      };
      if (editing) {
        await axios.put(`${API_URL}/api/cabex/clients/${editing.id}`, payload, { withCredentials: true });
        toast.success('Client updated');
      } else {
        await axios.post(`${API_URL}/api/cabex/clients`, payload, { withCredentials: true });
        toast.success('Client added');
      }
      setDialogOpen(false);
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/cabex/clients/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Client deleted');
      setDeleteTarget(null);
      fetchClients();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const monthlyPayout = (c) => {
    const ca = c.contract_amount || 0;
    const pct = c.client_percentage || 0;
    return ca && pct ? (ca * pct) / 100 : 0;
  };

  if (!currentCompany) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Please select a company</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cabex-clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Cabex Clients</h1>
          <p className="text-gray-500 mt-1">Manage Cabex contracts and payment terms</p>
        </div>
        <Button onClick={openAdd} data-testid="add-cabex-client-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search by name or Cabex ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-cabex"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : clients.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No Cabex clients yet</h3>
          <p className="text-gray-500 mb-4">Add your first contract to get started</p>
          <Button onClick={openAdd}>
            <Plus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="cabex-clients-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cabex ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contract Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Day</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Contract Amount</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Contract Fee</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% to Client</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monthly Payout</th>
                  <th className="px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {clients.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50" data-testid={`cabex-row-${c.id}`}>
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{c.client_name}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">{c.cabex_id || '-'}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">{c.contract_date || '-'}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">
                      {c.payment_day ? `${c.payment_day}${c.payment_day === 1 ? 'st' : c.payment_day === 15 ? 'th' : ''} of month` : '-'}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-right text-gray-900 font-medium">{fmtCurrency(c.contract_amount)}</td>
                    <td className="px-4 py-2.5 text-sm text-right text-gray-700">{fmtCurrency(c.contract_fee)}</td>
                    <td className="px-4 py-2.5 text-sm text-right text-gray-700">{fmtPercent(c.client_percentage)}</td>
                    <td className="px-4 py-2.5 text-sm text-right text-blue-700 font-medium">{fmtCurrency(monthlyPayout(c))}</td>
                    <td className="px-4 py-2.5">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(c)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDeleteTarget(c)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-gray-100 text-sm text-gray-500">
            {clients.length} client{clients.length === 1 ? '' : 's'}
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Cabex Client' : 'Add Cabex Client'}</DialogTitle>
            <DialogDescription>Enter contract details for this client.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="cabex-name">Client Name *</Label>
              <Input
                id="cabex-name"
                value={form.client_name}
                onChange={(e) => setForm((p) => ({ ...p, client_name: e.target.value }))}
                placeholder="e.g. Acme Corp"
                data-testid="cabex-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cabex-id">Cabex ID</Label>
              <Input
                id="cabex-id"
                value={form.cabex_id}
                onChange={(e) => setForm((p) => ({ ...p, cabex_id: e.target.value }))}
                placeholder="e.g. CBX-1234"
                data-testid="cabex-id-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cabex-contract-date">Contract Date</Label>
              <Input
                id="cabex-contract-date"
                type="date"
                value={form.contract_date}
                onChange={(e) => setForm((p) => ({ ...p, contract_date: e.target.value }))}
                data-testid="cabex-contract-date-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Payment Day</Label>
              <Select
                value={form.payment_day}
                onValueChange={(v) => setForm((p) => ({ ...p, payment_day: v }))}
              >
                <SelectTrigger data-testid="cabex-payment-day-select">
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1st of the month</SelectItem>
                  <SelectItem value="15">15th of the month</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cabex-amount">Contract Amount ($)</Label>
              <Input
                id="cabex-amount"
                type="number"
                step="0.01"
                value={form.contract_amount}
                onChange={(e) => setForm((p) => ({ ...p, contract_amount: e.target.value }))}
                placeholder="0.00"
                data-testid="cabex-amount-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cabex-fee">Contract Fee ($)</Label>
              <Input
                id="cabex-fee"
                type="number"
                step="0.01"
                value={form.contract_fee}
                onChange={(e) => setForm((p) => ({ ...p, contract_fee: e.target.value }))}
                placeholder="0.00"
                data-testid="cabex-fee-input"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="cabex-pct">Percentage to Client (monthly %)</Label>
              <Input
                id="cabex-pct"
                type="number"
                step="0.01"
                value={form.client_percentage}
                onChange={(e) => setForm((p) => ({ ...p, client_percentage: e.target.value }))}
                placeholder="e.g. 5"
                data-testid="cabex-pct-input"
              />
              {form.contract_amount && form.client_percentage && (
                <p className="text-xs text-gray-500">
                  Estimated monthly payout: {fmtCurrency((parseFloat(form.contract_amount) || 0) * (parseFloat(form.client_percentage) || 0) / 100)}
                </p>
              )}
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="cabex-notes">Notes</Label>
              <Textarea
                id="cabex-notes"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Optional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="save-cabex-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? 'Save changes' : 'Add Client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.client_name}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
